import { collection, addDoc, serverTimestamp, updateDoc, doc, runTransaction } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase/config";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PlaceOrderWebParams {
    amount: number;
    receiptNo: string;
    customer_id: string;
    customer_phone: string;
    returnUrl: string;
    customer_name?: string;
    customer_email?: string;
}

export interface PlaceOrderWebResult {
    success: boolean;
    orderId?: string;
    paymentSessionId?: string;
    transactionId?: string;
    reason?: string;
}

// Cashfree SDK mode — read from env (defaults to "sandbox")
const CF_ENV = (process.env.NEXT_PUBLIC_CASHFREE_ENV ?? "sandbox") as "sandbox" | "production";

// ─── Log to Firestore cashfreeLogs ────────────────────────────────────────────
export const logCashfree = async (data: {
    error: boolean;
    message: string;
    orderId?: string;
    extra?: Record<string, unknown>;
}) => {
    try {
        await addDoc(collection(db, "cashfreeLogs"), {
            ...data,
            time: serverTimestamp(),
        });
    } catch (e) {
        console.error("cashfree log error:", e);
    }
};

import type { Order } from "@/models/order";

// ─── Update order payment status in Firestore ─────────────────────────────────
export const updateOrderPayment = async (
    firestoreOrderId: string,
    data: Partial<Order>
) => {
    await updateDoc(doc(db, "orders", firestoreOrderId), {
        ...data,
        updatedAt: serverTimestamp(),
    });
};

// ─── Call Cloud Function via httpsCallable ────────────────────────────────────
const placeOrderWebCallable = httpsCallable<
    PlaceOrderWebParams,
    { payment_session_id: string; order_id: string }
>(functions, "placeOrderWeb");

// ─── Main exported function ───────────────────────────────────────────────────
/**
 * Calls the Firebase Cloud Function `placeOrderWeb` using httpsCallable,
 * then opens the Cashfree JS SDK checkout modal with the returned session ID.
 *
 * @param params - { amount, receiptNo, customer_id, customer_phone, returnUrl }
 * @returns { success, orderId, transactionId?, reason? }
 */
export const placeOrderWeb = async (
    params: PlaceOrderWebParams
): Promise<PlaceOrderWebResult> => {
    try {
        // 1. Call Cloud Function
        const cfResult = await placeOrderWebCallable({
            amount: params.amount,
            receiptNo: params.receiptNo,
            customer_id: params.customer_id,
            customer_phone: params.customer_phone,
            returnUrl: params.returnUrl,
            ...(params.customer_name && { customer_name: params.customer_name }),
            ...(params.customer_email && { customer_email: params.customer_email }),
        });

        const { payment_session_id, order_id } = cfResult.data;

        await logCashfree({
            error: false,
            message: "Cashfree order created",
            orderId: order_id,
        });

        // 2. Initialise Cashfree checkout with the session ID
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cashfree = (window as any).Cashfree({ mode: CF_ENV });

        // 3. Open payment modal
        const result = await cashfree.checkout({
            paymentSessionId: payment_session_id,
            redirectTarget: "_modal",
        });

        // 4. Handle result
        if (result?.error) {
            const reason = result.error?.message ?? "Payment failed";
            await logCashfree({ error: true, message: `cf error response 1: ${reason}`, orderId: order_id });
            return { success: false, orderId: order_id, reason };
        }

        if (result?.paymentDetails) {
            const transactionId = result.paymentDetails?.paymentMessage ?? order_id;
            await logCashfree({ error: false, message: "Payment successful", orderId: order_id });
            return {
                success: true,
                orderId: order_id,
                paymentSessionId: payment_session_id,
                transactionId,
            };
        }

        // Redirect-based flow
        return { success: true, orderId: order_id };

    } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        await logCashfree({ error: true, message: `cf error response 1: ${msg}` });
        return { success: false, reason: msg };
    }
};

// ─── Verify Cashfree Order & Update Flags ─────────────────────────────────────
export const verifyCashfreeOrder = async (
    firestoreOrderId: string,
    cfOrderId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result: any
) => {
    try {
        return await runTransaction(db, async (transaction) => {
            const orderRef = doc(db, "orders", firestoreOrderId);
            const orderSnap = await transaction.get(orderRef);

            if (!orderSnap.exists()) {
                throw new Error("Order not found");
            }

            const orderData = orderSnap.data();
            const outletDocId = orderData.outletDocId;

            let newOrderId = orderData.orderId || cfOrderId;
            const currentTransId = result?.transactionId || cfOrderId;

            if (result?.success || result?.paymentDetails) {
                // Payment was successful (paid -> 7--->8)
                let newFlag = 0;

                if (outletDocId) {
                    const flagsRef = doc(db, "outlet", outletDocId, "flags", "flags");
                    const flagsSnap = await transaction.get(flagsRef);

                    let currentCount = 0;
                    if (flagsSnap.exists()) {
                        currentCount = flagsSnap.data().count || 0;
                    }

                    const newCount = currentCount + 1;
                    newOrderId = String(newCount).padStart(4, "0"); // "0008"
                    newFlag = newCount;

                    transaction.set(flagsRef, { count: newCount }, { merge: true });
                }

                transaction.update(orderRef, {
                    isPaid: true,
                    orderId: newOrderId,
                    status: "Pending", // Set status to Pending
                    flag: newFlag, // "flags is 7--->8"
                    transId: currentTransId,
                    paidOn: serverTimestamp(),
                    paymentMethod: {
                        upi: {
                            upi_id: currentTransId,
                            channel: null
                        }
                    },
                    updatedAt: serverTimestamp(),
                });

                return { success: true, orderId: newOrderId, transId: currentTransId };
            } else {
                // Payment failed
                transaction.update(orderRef, {
                    isPaid: false,
                    status: "payment_failed",
                    updatedAt: serverTimestamp(),
                });

                return { success: false, reason: result?.reason || "Payment failed", orderId: cfOrderId };
            }
        });
    } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error("verifyCashfreeOrder error:", msg);
        return { success: false, reason: msg, orderId: cfOrderId };
    }
};
